import { Divider } from "antd";
import {
  Check,
  Eye,
  EyeOff,
  Fuel,
  Lock,
  ShoppingCart,
  UtensilsCrossed,
  X,
} from "lucide-react";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";

export default function LandingPage() {
  const navigate = useNavigate();

  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [hash]);

  return (
    <>
      <Helmet>
        <title>
          Family Expense Tracker - See What They Spend, Share Only What You Want
          | ExpenseHut
        </title>

        <meta
          name="description"
          content="See every expense your family makes, and share only the savings you choose. Keep the college fund hidden from the kids while everyone tracks spending together."
        />

        <meta
          name="keywords"
          content="expense tracker,family budget app,shared expenses,personal finance,hide savings from kids"
        />
      </Helmet>
      <div className="min-h-screen max--7xl mx-auto">
        {/* 1. Hero Section */}
        <section className="relative overflow-hidden pt-16 pb-20 px-6 lg:px-8 bg-gradient-to-b from-blue-50 via-slate-50 to-white">
          {/* Decorative background blobs */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-300/30 rounded-full blur-3xl" />
            <div className="absolute -bottom-32 -right-24 w-96 h-96 bg-emerald-300/30 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-5xl mx-auto text-center animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 rounded-full bg-white/80 backdrop-blur border border-blue-200 shadow-sm">
              <span className="text-sm font-semibold text-slate-700">
                The family budget app for parents
              </span>
            </div>

            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.1]">
              <span className="block text-red-700">Inflation is up.</span>
              <span className="block bg-gradient-to-r from-violet-600 to-blue-600 bg-clip-text text-transparent">
                See what your family spends —
              </span>
              <span className="block text-foreground">
                share only the savings you want.
              </span>
            </h1>

            <p className="mt-8 text-lg sm:text-2xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
              Track every expense together as a family. Show your spouse the
              emergency fund, keep the college fund hidden from the kids.
              <span className="font-semibold text-foreground">
                {" "}
                You see it all — they see what you choose.
              </span>
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                className="w-full sm:w-auto rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 px-8 py-3.5 text-white font-semibold text-lg shadow-lg shadow-blue-500/30 hover:shadow-xl hover:-translate-y-0.5 transition"
                onClick={() => navigate("/register")}
              >
                Start Free Today
              </button>
              <a
                href="#how-it-works"
                className="w-full sm:w-auto rounded-xl bg-white px-8 py-3.5 text-slate-700 font-semibold text-lg border border-slate-200 shadow-sm hover:border-slate-300 hover:-translate-y-0.5 transition"
              >
                See How It Works
              </a>
            </div>

            <p className="mt-6 text-sm text-muted-foreground">
              No credit card required • Free forever plan
            </p>
          </div>
        </section>

        {/* 2. The core idea: You see / They see */}
        <section className="py-16 px-6 lg:px-8 bg-white border-b border-slate-200">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-4">
              <span className="inline-block px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-sm font-semibold">
                How it's different
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-center text-foreground mb-4">
              One family. Two very different views.
            </h2>
            <p className="text-xl text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              You get the full picture of your family's money. Everyone else
              sees exactly what you decide — nothing more.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* You see */}
              <div className="rounded-2xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white p-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                    <Eye className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground">
                      You see
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      The parent's full view
                    </p>
                  </div>
                </div>
                <ul className="space-y-4">
                  {[
                    "Every expense your kids make — who, what, when",
                    "Every savings fund across the whole family",
                    "Spending by person, group, and trip",
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-foreground">{line}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* They see */}
              <div className="rounded-2xl border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-slate-600 rounded-xl flex items-center justify-center">
                    <EyeOff className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground">
                      They see
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      What your kids get to see
                    </p>
                  </div>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-foreground">
                      The shared grocery & household budget
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-foreground">
                      Their own expenses and allowance
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <X className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground line-through">
                      Your college fund
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <X className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground line-through">
                      Your emergency & mortgage savings
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 3. Organize by Groups */}
        <section className="py-16 px-6 lg:px-8 bg-gradient-to-b from-white to-blue-50/40 border-b border-slate-200">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-4">
              <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
                One family, many circles
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-center text-foreground mb-4">
              A Group for Everyone in Your Life
            </h2>
            <p className="text-xl text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Keep a separate space for family, parents, trips, or projects.
              Each group has its own expenses, its own members, and you decide
              who sees what.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Family Group */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl border border-blue-200 hover:shadow-lg transition">
                <div className=" bg-blue-200 rounded-xl flex items-center justify-center mb-4">
                  <img
                    src="/assets/family.png"
                    width={100}
                    className="flex-1"
                    alt="family"
                  />
                </div>

                <div className="p-3">
                  <h3 className="text-xl font-bold text-foreground mb-3">
                    Family
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Track shared household expenses
                  </p>
                  <p className="text-2xl font-bold text-blue-600 mb-4">
                    $2,450
                  </p>
                  <div className="space-y-2 text-xs text-muted-foreground">
                    <p>• Grocery shopping</p>
                    <p>• Electricity bills</p>
                    <p>• Utilities & Internet</p>
                  </div>
                </div>
              </div>

              {/* Parents Group */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-2xl border border-purple-200 hover:shadow-lg transition">
                <div className=" bg-blue-200 rounded-xl flex items-center justify-center mb-4">
                  <img
                    src="/assets/parents.png"
                    width={100}
                    className="flex-1"
                    alt="parents"
                  />
                </div>

                <div className="p-3">
                  <h3 className="text-xl font-bold text-foreground mb-3">
                    Parents
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Manage parent contributions
                  </p>
                  <p className="text-2xl font-bold text-purple-600 mb-4">
                    $1,850
                  </p>
                  <div className="space-y-2 text-xs text-muted-foreground">
                    <p>• Medical expenses</p>
                    <p>• Monthly transfers</p>
                    <p>• Healthcare costs</p>
                  </div>
                </div>
              </div>

              {/* Vacation Trip Group */}
              <div className="bg-gradient-to-br from-pink-50 to-pink-100/50 rounded-2xl border border-pink-200 hover:shadow-lg transition">
                <div className=" bg-blue-200 rounded-xl flex items-center justify-center mb-4">
                  <img
                    src="/assets/vacation.png"
                    width={100}
                    alt="vacation"
                    className="flex-1"
                  />
                </div>
                <div className="p-3">
                  <h3 className="text-xl font-bold text-foreground mb-3">
                    Vacation Trip
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Track vacation expenses
                  </p>
                  <p className="text-2xl font-bold text-pink-600 mb-4">
                    $3,200
                  </p>
                  <div className="space-y-2 text-xs text-muted-foreground">
                    <p>• Uber & Transport</p>
                    <p>• Food & Dining</p>
                    <p>• Activities & Bars</p>
                  </div>
                </div>
              </div>

              {/* Ledger Group */}
              <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-2xl border border-green-200 hover:shadow-lg transition">
                <div className=" bg-blue-200 rounded-xl flex items-center justify-center mb-4">
                  <img
                    src="/assets/ledger.png"
                    width={100}
                    className="flex-1"
                    alt="ledger"
                  />
                </div>

                <div className="p-3">
                  <h3 className="text-xl font-bold text-foreground mb-3">
                    Ledger
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Long-term projects
                  </p>
                  <p className="text-2xl font-bold text-green-600 mb-4">
                    $45,000
                  </p>
                  <div className="space-y-2 text-xs text-muted-foreground">
                    <p>• Mortgage payments</p>
                    <p>• Property taxes</p>
                    <p>• Home improvements</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Smart Savings Sharing */}
        <section className="py-16 px-6 lg:px-8 bg-gradient-to-br from-emerald-50 to-green-50 border-b border-slate-200">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-4">
              <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold">
                The part only you control
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-center text-foreground mb-4">
              Share Savings with Some, Hide from Others
            </h2>
            <p className="text-xl text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Show your emergency fund to your spouse, keep the college fund
              hidden from the kids. You control exactly who sees each number.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Shared with Spouse */}
              <div className="bg-white rounded-2xl border border-green-200 shadow-lg">
                <div className=" gap-3 mb-3">
                  <div className=" bg-blue-200 rounded-xl flex items-center justify-center mb-4">
                    <img
                      src="/assets/landingpage/spouse.png"
                      width={100}
                      className="flex-1"
                      alt="spouse"
                    />
                  </div>
                  <h3 className="text-2xl px-4 font-bold text-foreground flex gap-2 items-center">
                    <Check className="text-green-400" />
                    Visible to Spouse
                  </h3>
                </div>
                <div className="space-y-4 p-4">
                  <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                    <p className="text-sm text-muted-foreground mb-1">
                      Emergency Fund
                    </p>
                    <p className="text-2xl font-bold text-foreground">$8,500</p>
                    <p className="text-xs text-green-600 mt-2">
                      ✓ Spouse can see
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                    <p className="text-sm text-muted-foreground mb-1">
                      Vacation Fund
                    </p>
                    <p className="text-2xl font-bold text-foreground">$3,950</p>
                    <p className="text-xs text-green-600 mt-2">
                      ✓ Spouse can see
                    </p>
                  </div>
                </div>
              </div>

              {/* Hidden from Kids */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-lg">
                <div className="items-center gap-3">
                  <div className=" bg-blue-200 rounded-xl flex items-center justify-center mb-4">
                    <img
                      src="/assets/landingpage/kids.png"
                      width={100}
                      className="flex-1"
                      alt="kids"
                    />
                  </div>

                  <h3 className="text-2xl font-bold text-foreground flex gap-3 items-center px-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <Lock className="w-6 h-6 text-red-600" />
                    </div>
                    Hidden from Kids
                  </h3>
                </div>
                <div className="space-y-4 p-4">
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                    <p className="text-sm text-muted-foreground mb-1">
                      Mortgage Fund
                    </p>
                    <p className="text-2xl font-bold text-foreground">
                      $25,000
                    </p>
                    <p className="text-xs text-slate-600 mt-2">
                      🔒 Kids cannot see
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                    <p className="text-sm text-muted-foreground mb-1">
                      College Fund
                    </p>
                    <p className="text-2xl font-bold text-foreground">
                      $18,500
                    </p>
                    <p className="text-xs text-slate-600 mt-2">
                      🔒 Kids cannot see
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 5. See All Family Expenses at a Glance */}
        <section className="py-16 px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-4">
              <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
                Track Together
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-center text-foreground mb-4">
              Nothing Your Kids Spend Slips Past You
            </h2>
            <p className="text-xl text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Know who spent what, when, and where — in real time. You get the
              full picture, even as their savings stay yours to share.
            </p>

            {/* Recent Transactions Card */}
            <div className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-100 to-blue-50 border-b border-slate-200 px-8 py-6">
                <h3 className="text-2xl font-bold text-foreground">
                  Recent Family Transactions
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Your family's spending today
                </p>
              </div>

              {/* Transactions List */}
              <div className="divide-y divide-slate-200">
                {/* Transaction 1 - Kid's Burger */}
                <div className="px-8 py-6 hover:bg-slate-50 transition">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <UtensilsCrossed className="w-7 h-7 text-yellow-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-foreground">
                          Burger from FastFood
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500 text-white text-sm font-bold rounded-lg shadow-md">
                            👦 <span>Your Son</span>
                          </span>
                          <span className="text-xs text-muted-foreground">
                            • Food & Dining
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <p className="text-xl font-bold text-foreground">$9.99</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        2 hours ago
                      </p>
                    </div>
                  </div>
                </div>

                {/* Transaction 2 - Dad's Gas */}
                <div className="px-8 py-6 hover:bg-slate-50 transition">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Fuel className="w-7 h-7 text-orange-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-foreground">
                          Gas Station Fill-up
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500 text-white text-sm font-bold rounded-lg shadow-md">
                            👨 <span>You (Dad)</span>
                          </span>
                          <span className="text-xs text-muted-foreground">
                            • Transport
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <p className="text-xl font-bold text-foreground">
                        $50.00
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        5 hours ago
                      </p>
                    </div>
                  </div>
                </div>

                {/* Transaction 3 - Mom's Groceries */}
                <div className="px-8 py-6 hover:bg-slate-50 transition">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <ShoppingCart className="w-7 h-7 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-foreground">
                          Weekly Groceries
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-pink-500 text-white text-sm font-bold rounded-lg shadow-md">
                            👩 <span>Mom</span>
                          </span>
                          <span className="text-xs text-muted-foreground">
                            • Food & Household
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <p className="text-xl font-bold text-foreground">
                        $27.50
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        1 day ago
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Summary */}
              <div className="bg-gradient-to-r from-slate-50 to-blue-50/50 border-t border-slate-200 px-8 py-6">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      Today's Total
                    </p>
                    <p className="text-2xl font-bold text-blue-600">$87.49</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      Family Members
                    </p>
                    <p className="text-2xl font-bold text-foreground">3</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      Categories
                    </p>
                    <p className="text-2xl font-bold text-foreground">3</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 6. Track Trips Separately */}
        <section className="py-16 px-6 lg:px-8 bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-4">
              <span className="inline-block px-3 py-1 rounded-full bg-pink-100 text-pink-700 text-sm font-semibold">
                Trips & Travel
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-center text-foreground mb-4">
              Keep Every Trip Separate
            </h2>
            <p className="text-xl text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Set a budget per trip and see your spending breakdown by
              destination — without mixing it into your everyday expenses.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  name: "Italy Vacation",
                  duration: "7 days",
                  spent: "$1,850",
                  budget: "$2,500",
                  color: "from-red-400 to-orange-500",
                },
                {
                  name: "Houston Visit",
                  duration: "5 days",
                  spent: "$650",
                  budget: "$800",
                  color: "from-blue-400 to-cyan-500",
                },
                {
                  name: "Business Trip",
                  duration: "3 days",
                  spent: "$1,200",
                  budget: "$1,500",
                  color: "from-green-400 to-emerald-500",
                },
                {
                  name: "Summer Vacation",
                  duration: "10 days",
                  spent: "$3,400",
                  budget: "$4,000",
                  color: "from-yellow-400 to-orange-400",
                },
              ].map((trip, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition"
                >
                  <div
                    className={`bg-gradient-to-r ${trip.color} h-24 flex items-end p-4`}
                  >
                    <h3 className="text-lg font-bold text-white">
                      {trip.name}
                    </h3>
                  </div>
                  <div className="bg-white p-6 space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Duration: {trip.duration}
                    </p>
                    <div className="mb-3">
                      <p className="text-xs text-muted-foreground mb-1">
                        Total Expenses
                      </p>
                      <p className="text-2xl font-bold text-foreground">
                        {trip.spent}
                      </p>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-sm font-semibold text-foreground">
                          Budget
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {trip.budget}
                        </p>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{
                            width: `${(parseFloat(trip.spent.replace("$", "")) / parseFloat(trip.budget.replace("$", ""))) * 100}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 7. How It Works */}
        <section id="how-it-works" className="py-16 px-6 lg:px-8 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl sm:text-5xl font-bold text-center text-foreground mb-12">
              Get Started in Minutes
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                {
                  n: 1,
                  title: "Sign Up",
                  desc: "Create your free account in seconds.",
                },
                {
                  n: 2,
                  title: "Create Groups",
                  desc: "Add family, parents, or a trip and invite members.",
                },
                {
                  n: 3,
                  title: "Track Together",
                  desc: "Log expenses and savings — everyone stays in sync.",
                },
                {
                  n: 4,
                  title: "Control Access",
                  desc: "Choose exactly who sees each expense and savings goal.",
                },
              ].map((step, idx) => (
                <div
                  key={step.n}
                  className={`rounded-2xl p-8 text-center h-full flex flex-col justify-center border ${
                    idx === 0
                      ? "bg-gradient-to-br from-violet-100 to-blue-100 border-blue-200"
                      : "bg-white border-slate-200"
                  }`}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-violet-600 to-blue-600 text-white rounded-full flex items-center justify-center font-bold text-2xl mx-auto mb-4">
                    {step.n}
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 8. Pricing */}
        <section
          id="pricing"
          className="flex py-16 px-6 lg:px-8 bg-white justify-center"
        >
          <div className="max-w-sm rounded-2xl border border-gray-200 p-6 shadow-sm bg-white">
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">
              Pricing
            </h3>

            <p className="text-gray-600 mb-6">
              Track expenses your way — automatic or manual.
            </p>

            <div className="mb-6">
              <div className="text-3xl font-bold text-gray-900">Free</div>
              <p className="text-sm text-gray-600 mt-1">
                Manual expense entry or upload spreadsheets
              </p>
            </div>

            <div className="border-t border-gray-200 pt-4 mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-gray-900">$1.29</span>
                <span className="text-sm text-gray-600">
                  / per month / connected account
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Automatically sync expenses from your bank
              </p>
            </div>

            <ul className="space-y-2 text-sm text-gray-700 mb-6">
              <li>• Unlimited manual entries</li>
              <li>• Bank-level encryption</li>
              <li>• Cancel anytime</li>
            </ul>

            <button
              className="w-full rounded-xl bg-green-600 py-2.5 text-white font-medium hover:bg-green-700 transition"
              onClick={() => navigate("/register")}
            >
              Get Started
            </button>
          </div>
        </section>

        <Divider />

        {/* 9. Final CTA */}
        <section className="py-20 px-6 lg:px-8 bg-gradient-to-r from-violet-600 to-blue-600 text-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6">
              See everything. Share what you want.
            </h2>
            <p className="text-xl opacity-90 mb-8">
              Track your whole family's spending together, and keep your savings
              yours to reveal. Start free today, no credit card required.
            </p>
            <button
              className="rounded-xl bg-white px-8 py-3.5 text-blue-700 font-semibold text-lg shadow-lg hover:-translate-y-0.5 transition"
              onClick={() => navigate("/register")}
            >
              Start Free Today
            </button>
          </div>
        </section>
      </div>
    </>
  );
}
